// packages/console/src/components/panels/LivePresetPicker.tsx
//
// Live-preset picker for the LIVE console. It lists the workspace's live presets
// (mode==="live") and selects the one the Rendition panel below edits. It is the
// live-mode analogue of the file console's ProfileMenu, trimmed to a selection
// list: the live ladder is defined by the picked preset.
//
// Honest states (R10): loading shows a loading line, a load error shows the
// message, and an empty workspace (no live presets yet) says so rather than
// fabricating a default.

import { Panel } from "../Parts";
import { Icon } from "../Icons";
import type { Preset } from "../../api/jobs";

export function LivePresetPicker({
  presets,
  activeId,
  onChange,
  loading,
  errorMessage,
}: {
  presets: Preset[];
  activeId: string | null;
  onChange: (id: string) => void;
  loading: boolean;
  errorMessage: string | null;
}) {
  return (
    <Panel title="Live presets" style={{ flex: "none" }}>
      {loading ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>Loading live presets...</div>
      ) : errorMessage ? (
        <div style={{ font: "var(--t-micro)", color: "var(--err)" }}>{errorMessage}</div>
      ) : presets.length === 0 ? (
        <div style={{ font: "var(--t-micro)", color: "var(--fg4)" }}>No live presets yet for this workspace.</div>
      ) : (
        <div
          role="listbox"
          aria-label="Live preset"
          style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 176, overflowY: "auto" }}
        >
          {presets.map((p) => {
            const active = p.id === activeId;
            return (
              <button
                key={p.id}
                className="ae-b"
                role="option"
                aria-selected={active}
                onClick={() => onChange(p.id)}
                title={p.name}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  width: "100%",
                  minHeight: 34,
                  padding: "0 10px",
                  borderRadius: "var(--r-sm)",
                  cursor: "pointer",
                  textAlign: "left",
                  font: "400 12px var(--font-mono)",
                  color: active ? "#fff" : "var(--fg2)",
                  background: active ? "var(--blue-tint)" : "transparent",
                  border: `1px solid ${active ? "var(--blue-500)" : "var(--line-strong)"}`,
                }}
              >
                {active && <Icon name="check" size={13} color="var(--blue-300)" />}
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.name}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </Panel>
  );
}
